<template>

  <section>

    <!-- XOL-371: the "view only" banner is shown ONLY to actual view-only users
         (viewer=true) — they are the sole Tower Co users with no edit access to
         anything. Regular / manager / approver users may still upload files and
         edit non-role-gated fields even when they cannot action workflow steps,
         so the previous !canWriteStep() condition mislabelled them as view-only. -->
    <el-row v-if="isViewOnly()">
      <el-col :span="24">
        <el-alert
          type="info"
          title="View only — you do not have permission to make changes to this request."
          show-icon
          :closable="false" />
      </el-col>
    </el-row>

    <!-- XOL-526: shown only to a backhaul provider viewing its own RTB (never
         the Tower Co, the telco owner, or a Backhaul org that IS organisation_id).
         S3 (edit) appears in the body only when isCounterpartyWriter() is true
         for this viewer — see providerBannerBody(). -->
    <el-row v-if="isProviderOnlyViewer()">
      <el-col :span="24">
        <el-alert
          type="info"
          :title="'You are viewing this ' + actionData.actionType + ' as its backhaul provider.'"
          show-icon
          :closable="false">
          <span>{{providerBannerBody()}}</span>
        </el-alert>
      </el-col>
    </el-row>

    <el-row>
      <el-col :span="24">
        <el-alert v-if="!actionData.emails.sendMail"
          type="info"
          :title="'This ' + actionData.actionType + ' won\'t send e-mails'"
          description=""
          show-icon
          :closable="false">
            In the email settings, send email has been disabled. A manager level user can turn it on.&nbsp;
            <el-switch v-if="isTowerCo() && isManager()" v-model="actionData.emails.sendMail" />
          </el-alert>
        </el-col>

        <el-row>

          <el-col :span="24" align="center">

            <h4 :class="titleColour()">
              <small><i :class="actionData.workflow.icon"></i> {{actionData.workflow.name}}:</small>
               {{actionData.site.siteName}}
            </h4>

            <span v-if="actionData.organisation.colourCode && actionData.organisation.shortName">
              <org-badge class="pull-right" :organisation="actionData.organisation" />
            </span>
            <div v-else>
              <br />

              <span class="text-muted"><small>by: <b>{{actionData.organisation.name}}</b></small></span>
            </div>

          </el-col>
        </el-row>

        <el-row>
          <el-col v-if="isTablet || isDesktop" :span="24">
            <el-steps :active="activeStep-1" finish-status="success" align-center
            :direction="!(isTablet || isDesktop) ? 'vertical' : 'horizontal'">

              <el-step v-for="s in steps" :key="s.title" :description="s.title">
                <template #icon>
                  <i :class="s.icon" style="background: inherit;"></i>
                </template>
              </el-step>  <!-- :title="s.title" -->

            </el-steps>
          </el-col>

          <el-row>
              <el-col :span="24">

                <!-- XOL-393: default to the VIEWER's own party. With no v-model the
                     party tabs always opened on party 1 (the Tower Co), so a telco
                     opening a request saw the Tower Co's files and — because the panes
                     are `lazy` — its own pane was not rendered at all until clicked.
                     Panes need an explicit :name or Element UI names them positionally
                     ("0","1") and the partyNo model could never match. -->
                <el-tabs type="border-card" v-model="selectedPartyTab">

                  <el-tab-pane  v-for="partyNo in actionData.parties" :key="partyNo" :name="String(partyNo)" :label="partyTabLabel(partyNo)" lazy>

                    <el-tabs v-model="selectedFileTab" @tab-click="onFileTabClick">
                      <el-tab-pane  v-for="(s, ind) in steps" :name="'fileStep' + Number(ind + 1)" :key="s.id" :disabled="reachedStep < Number(ind + 1)" lazy>
                        <template #label><i :class="s.icon">&nbsp;</i><span v-if="isDesktop">{{s.title}}</span></template>

                          <files-table
                            :title="s.title + (!partyOrg(partyNo) ? ': ' + partyName(partyNo) : '')"
                            :id="getFileTableRef(partyNo)"

                            :name="getFileTableRef(partyNo)"
                            :table-ref="'filestable' + partyNo"
                            :org-badge="partyOrg(partyNo)"

                            :site-id="actionData.site_id"
                            :type="actionData.actionType.toLowerCase()"
                            :type-id="actionData.id"
                            :step="ind + 1"
                            :partyNo="partyNo"
                            :step-data="step"
                            show-refresh
                            :show-search="false"
                            :disable-form="!isActive(actionData)"
                            size="small"
                            :upload-info="requestsInfo"
                            :upload-callback="afterUpload"
                            :upload-locked="partyStepCompleted(ind + 1)"
                            :allow-add-category="isTowerCo() || (Number(partyNo) === viewerPartyNo() && (ind + 1) === activeStep && !partyOneStepCompleted(ind + 1) && isActive(actionData))"
                            />

                    </el-tab-pane>
                    </el-tabs>

                    <!-- XOL-526 (D3 files; UX §1.7(b)): the Telco's lighter equivalent
                         of the provider banner — makes the party-2 sharing visible
                         without a banner on every viewer. -->
                    <p v-if="Number(partyNo) === 2 && hasDistinctProvider()" class="text-muted">
                      <small><i class="fa fa-users" aria-hidden="true"></i> {{sharedFilesCue()}}</small>
                    </p>

                  </el-tab-pane>
                </el-tabs>
            </el-col>

        </el-row>

        <br />

        </el-row>

        <el-collapse v-model="show" accordion>
          <el-collapse-item name="map">
            <template #title>
                <span class="text-info">Map&nbsp;<i class="header-icon fa fa-map-marker"></i></span>
              </template>
              <site-map :row-data="actionData" />
          </el-collapse-item>


          <el-collapse-item name="workflow">
            <template #title>
                <span class="text-info">Workflow&nbsp;<i class="header-icon fa fa-ellipsis-h"></i></span>
              </template>

              <el-row>
                <el-col :offset="1" :span="21">
                <center><h4 v-if="steps[activeStep-1]"><i :class="steps[activeStep-1].icon"></i> {{steps[activeStep-1].title}}</h4> <small class="text-muted">| Step {{activeStep}} of {{steps.length}}</small></center>
                <br />

             <!-- XOL-526 (UX U-G): honest-disabled explanation for a counterparty
                  whose own party-2 content is locked because the Tower Co's
                  party-1 row for this step is complete. Also applies to the
                  Telco today, not just the provider. -->
             <el-row v-if="!isTowerCo() && partyOneStepCompleted(activeStep)">
               <el-col :span="24">
                 <el-alert
                   type="success"
                   show-icon
                   :closable="false"
                   center
                   :title="'This step is complete. Uploads and edits are locked until ' + towerCoName() + ' re-opens it.'" />
               </el-col>
             </el-row>

             <el-row :gutter="16">
               <el-col :span="22" :offset="2">
                 <el-form :model="state">
                   <component :is="comp" @updated="stepUpdated" :row-data="rowData" :row-index="rowIndex" :step-data="step" :read-only="readOnly" />
                </el-form>
                </el-col>
               </el-row>

                 <el-row v-if="isTowerCo()">

                   <el-col v-if="activeStep > 1" :span="4" align="center">
                      <el-button type="text" @click="prevStep()">&lt;&nbsp;Previous Step</el-button>
                   </el-col>

                   <el-col :span="8" :push="activeStep > 1 ? 4 : 8" align="center">
                     <el-alert show-icon :type="step.completed ? 'success' : 'warning'" center :closable="false">
                       <check-box v-if="isTowerCo() && isManager()" v-model="step.completed" no-form @change="onCompleteToggle" :disabled="!canWriteStep() || !isActive(actionData)">Complete Step</check-box>
                       &nbsp;<span v-if="step.completed">on <b>{{formatDateTime(step.completed_at)}}</b> by <b>{{step.completed_by}}</b></span>
                       <span v-else>Step Pending Approval</span>
                     </el-alert>
                   </el-col>

                   <el-col :span="(activeStep > 1 ? 12 : 16)"  :pull="1" align="right">
                     <el-button v-if="activeStep <= steps.length" type="info" @click="nextStep()" :disabled="!canWriteStep() || !validateStep(activeStep) || savingStep" :loading="savingStep" :aria-disabled="!canWriteStep() || !validateStep(activeStep) || savingStep">Finish Step <b>{{activeStep}}</b> / {{steps.length}}</el-button>
                     <el-button v-if="isTowerCo() && activeStep === steps.length && step.approved" type="success" @click="completeWorkflow()" :disabled="!canWriteStep() || !validateStep(activeStep)">Complete {{actionData.actionType}}</el-button>
                   </el-col>
                 </el-row>

            </el-col>
          </el-row>
        </el-collapse-item>

  </el-collapse>
  </el-row>

  <el-row>
    <el-col :span="4">
      <small class="text-muted">{{actionData.actionType}} # <b>{{actionData.id}}-{{step.partyNo}}-{{step.stepNo}}/{{step.id}}</b></small>
    </el-col>

    <el-col :span="20" align="right">

      <!-- XOL-526: a provider never has an emails list of its own (D10 ii) —
           nothing replaces this for it; banner S2 covers how it receives
           notices instead. XOL-535 (S6, UX §2.6.2): a Telco on RTS/RTU/RTD
           (D9 is RTB-only) who can't edit the telco recipient list gets a
           read-only heading and button copy, and CAN still open the popup to
           see it. A view-only Telco on an RTB also computes emailsReadOnly
           true, but never sees the difference — Popup.vue already disables
           the trigger button for any view-only user, so the popup never
           opens for them regardless of this heading/copy. -->
      <div v-if="showEmailSetup">
        <popup :heading="emailsReadOnly ? 'Notification Emails' : 'Configure Emails'"
          :button-text="emailsReadOnly ? 'View Emails' : (configNeeded ? 'Emails Setup' :  'Emails Update')"
          :button-style="emailsReadOnly ? 'text' : (configNeeded ? 'danger' : 'text')"
          :show-save="false"
          :center-button="true"
          button-icon="fa fa-envelope">

        <email-setup :row-data="actionData" :rtx-info="rtxInfo" />

      </popup>
    </div>
  </el-col>

  </el-row>

</section>
</template>


<script>


import RequestMixins from '@/mixins/RequestMixins'
import DrawerBusyMixin from '@/mixins/DrawerBusyMixin'

import EmailSetup from '@/components/requests/EmailSetup'
import FilesTable from '@/components/tables/FilesTable'

import step1 from '@/components/requests/workflow/step1'
import step2 from '@/components/requests/workflow/step2'
import step3 from '@/components/requests/workflow/step3'
import step4 from '@/components/requests/workflow/step4'
import step5 from '@/components/requests/workflow/step5'

import SiteMap from '@/components/requests/SiteMap'


export default {
  // No DrawerDirtyMixin: the workflow auto-saves each step on unmount/navigation
  // (step.beforeDestroy → saveStep), so closing the drawer never loses data —
  // and the only available dirty signal (stepUpdated, driven by a step's
  // beforeDestroy `updated` emit) fires on lifecycle, giving false positives on
  // open. A real per-field edit guard for the stepper is a separate follow-up.
  // DrawerBusyMixin DOES apply: while a step save is in flight (`savingStep`),
  // the drawer must not close (it would race the save) — see `drawerBusy`.
  mixins: [RequestMixins, DrawerBusyMixin],

  components: {
    EmailSetup,
    FilesTable,
    step1,
    step2,
    step3,
    step4,
    step5,
    SiteMap
  },

  props: {

    rowData: {
      type: Object,
      default: null
    },

    rowIndex: {
      type: Number,
      default: null
    },

    isNew: {
      type: Boolean,
      default: false
    },

    closeDialog: {
      type: Function,
      default: null
    }
  },

  methods: {

    getFileTableRef (pNo) {
      return 'filestable' + this.activeStep + '-' + pNo
    },

    // XOL-107/XOL-344/XOL-526: is the party-1 (Tower Co) step for stepNo
    // complete? The check itself moved to RequestMixins.partyOneStepCompleted
    // (step1-5 and other request surfaces need it too) — this delegates so
    // the existing `:upload-locked="partyStepCompleted(...)"` binding is
    // untouched.
    partyStepCompleted (stepNo) {
      return this.partyOneStepCompleted(stepNo)
    },

    // XOL-526 (UX §2.1): the provider banner body, up to four sentences.
    // S3 (edit) only appears when isCounterpartyWriter() is true for this viewer.
    providerBannerBody () {
      var sentences = []

      sentences.push('Files you upload go to the ' + this.partyTabLabel(2) +
        ' tab and are labelled with your organisation. ' + this.actionData.organisation.name +
        ' and ' + this.towerCoName() + ' can see them, and you can see theirs.')

      if (this.actionData.emails && this.actionData.emails.sendMail) {
        sentences.push('Each upload is e-mailed to the other organisations on this request. You receive these notices if Upload Alerts is on in your User Profile.')
      }

      if (this.isCounterpartyWriter()) {
        sentences.push('You can also edit the shared remarks and the Technical Proposal type.')
      }

      sentences.push(this.towerCoName() + ' manages the workflow steps.')

      return sentences.join(' ')
    },



    validateStep (stepNo) {

      let moveOn = false

      switch (stepNo) {
        case 1:
          moveOn = this.step.completed
          break
        case 2:
          moveOn = this.step.completed // && this.step.approved
          break
        case 3:
          moveOn = this.step.completed
          break
        case 4:
          moveOn = this.step.completed
          break
        case 5:
          moveOn = this.step.completed
          break
        default:
          moveOn = false
          break
      }

      return moveOn
    },



    nextStep () {

      if (!this.validateStep(this.activeStep)) {
        return
      }

      if (!this.actionData.site_id || !this.isActive(this.actionData)) {
        return
      }

      var nextActiveStep = this.activeStep + 1

      if (nextActiveStep > this.steps.length) {
        return
      }

      var snapshot = {
        currentStep: this.actionData.currentStep,
        status: this.actionData.status,
        activeStep: this.activeStep,
        reachedStep: this.reachedStep,
        step: this.step,
        comp: this.comp
      }

      this.actionData.currentStep = Math.max(nextActiveStep, this.actionData.currentStep)
      this.actionData.status = this.steps[nextActiveStep - 1].title
      this.activeStep = nextActiveStep
      this.reachedStep = Math.max(nextActiveStep, this.reachedStep)
      this.changeStep(nextActiveStep)

      this.savingStep = true

      var self = this
      this.saveRequest(this.actionData, function (response) {
        self.savingStep = false
      }, null, function (err) {
        self.savingStep = false
        self.actionData.currentStep = snapshot.currentStep
        self.actionData.status = snapshot.status
        self.activeStep = snapshot.activeStep
        self.reachedStep = snapshot.reachedStep
        self.step = snapshot.step
        self.comp = snapshot.comp
        self.changeStep(snapshot.activeStep)
        self.$notify({
          type: 'error',
          title: 'Couldn\'t advance step',
          message: 'We couldn\'t save the step change. Please check your connection and try again.',
          duration: 5500
        })
        console.error('nextStep save failed, rolled back to step', snapshot.activeStep, err)
      }, true)
    },



    prevStep () {
      this.activeStep -= 1

      if (this.activeStep <= 0) {
        this.activeStep = 1
      }

      this.changeStep(this.activeStep)
    },


    // XOL-534 (B1): a child step component emits `updated` from its OWN
    // `beforeDestroy()` on every unmount — including when it's the OUTGOING
    // component of a file-tab / step navigation that `changeStep()` already
    // resolved to a DIFFERENT step. Vue mounts the new component (reading
    // `this.step` at the CORRECT, just-set value) before it unmounts the old
    // one, but the old one's stale `updated` emit then reassigns `this.step`
    // — and RequestMixins' `stepData` watcher pushes that corrupted value
    // straight into the already-mounted new child's local `step`. Only
    // accept an emit that actually matches the currently active step/party.
    //
    // XOL-534 round 3 (reviewer correction): `saveStepData(data)` below is
    // still called for parity with the pre-XOL-526 shape of this method, but
    // it is a documented pre-existing NO-OP, not a "steps[] sync" — see
    // saveStepData's own comment and plan §8a (follow-up: delete both).
    stepUpdated (data) {
      if (!data) return

      this.saveStepData(data)

      if (Number(data.stepNo) === this.activeStep && Number(data.partyNo) === this.viewerPartyNo()) {
        this.step = data
      }
    },



    changeStep (stepNo) {

      if (this.canWriteStep() && this.step) {
        this.saveStep(this.step)
      }

      // XOL-393: never let `step` go null — stepData() returns null when no row matches,
      // and the template dereferences step.completed unguarded. That null was the
      // blank-drawer crash.
      //
      // ⚠ The `stepNo - 1` argument here is DEAD. stepData(current = 1) discards it
      // whenever `this.activeStep` is truthy (`if (this.activeStep) current = this.activeStep`),
      // so the lookup always resolves off activeStep, not off this parameter. It reads
      // like a deliberate off-by-one and is not: "correcting" stepData to honour its
      // argument would make changeStep(5) resolve step FOUR's data while displaying
      // step 5. Left as-is rather than changed under this ticket, but do not trust it.
      //
      // This is also why clamping activeStep in created() is what actually fixes the
      // crash — stepData reads that value. The guard below is defence-in-depth.
      var nextStep = this.stepData(stepNo - 1)
      if (nextStep) { this.step = nextStep }

      if (stepNo > this.actionData.currentStep) { this.actionData.currentStep = stepNo }
      if (stepNo > this.reachedStep) { this.reachedStep = stepNo }
      this.selectedFileTab = 'fileStep' + stepNo

      switch (stepNo) {
        case 1:
          this.comp = step1
          break
        case 2:
          this.comp = step2
          break
        case 3:
          this.comp = step3
          break
        case 4:
          this.comp = step4
          break
        case 5:
          this.comp = step5
          break
        default:
          this.comp = step1
      }

    },


    onFileTabClick (pane) {
      const n = Number(String(pane.name || pane.paneName || '').replace('fileStep', ''))

      if (!n || Number.isNaN(n) || n === this.activeStep) {
        return
      }

      if (n > this.reachedStep) {
        return
      }

      this.activeStep = n
      this.changeStep(n)
    },



    completeStep () {
      //    this.step2p1 = this.findWorkflowStep(2, 1, this.steps)

      if (this.step.completed) {
        this.step.completed_at = this.formatDateTimeSql(new Date())
        this.step.completed_by = this.userName()
      } else {
        this.step.completed_at = null
        this.step.completed_by = null
      }

      this.$emit('updated', this.step)
    },


    // XOL-107/XOL-344: toggling "Complete Step" warns + persists immediately so
    // the change isn't lost when the manager doesn't navigate away (the lifecycle
    // saveStep only fires on navigation/close). Completing locks the step (telco
    // can no longer upload files or make changes); un-ticking re-opens it.
    onCompleteToggle () {
      if (this.step.completed) {
        this.$confirm(
          'Once this step is completed, the telco will no longer be able to upload files or make changes to it until the step is re-opened. Continue?',
          'Complete this step?',
          { confirmButtonText: 'Complete', cancelButtonText: 'Cancel', type: 'warning' }
        ).then(() => {
          this.completeStep()
          if (this.canWriteStep()) {
            this.saveStep(this.step)
          }
        }, () => {
          this.step.completed = false
        })
      } else {
        this.$confirm(
          'This will mark the step incomplete and re-open it so the telco can upload files and changes can be made. Continue?',
          'Re-open this step?',
          { confirmButtonText: 'Re-open', cancelButtonText: 'Cancel', type: 'warning' }
        ).then(() => {
          this.completeStep()
          if (this.canWriteStep()) {
            this.saveStep(this.step)
          }
        }, () => {
          this.step.completed = true
        })
      }
    },


    saved (response) {
      if (response && !response.warning) {
        if (this.actionData && !this.actionData.id) {
        }
      }
    },



    completeWorkflow () {

      if (this.isNew) {

        if (this.isActive(this.actionData)) {
          this.saveRequest(this.actionData, this.saveAndClose)
        }

      } else {

        if (this.isTowerCo() && this.isManager() && this.step.approved) {

          this.step.completed = true
          this.step.completed_at = this.formatDateTimeSql(new Date())
          this.step.completed_by = this.userName()
          this.actionData.status = this.step.approvedStatus === 'Approved' ? 'Closed' : this.step.approvedStatus
          this.actionData.approved_at = this.step.completed_at
          this.actionData.approved_by = this.userName()

          this.saveRequest(this.actionData)
          this.refreshStats(this.org().id, true)
        }

      }

    },



    saveAndClose (response) {
      if (this.closeDialog) {
        this.closeDialog()
      }

    },



    stepData (current = 1) {

      if (this.rowData.steps && this.rowData.steps.length > 0) {

        if (this.activeStep) {
          current = this.activeStep
        }

        // XOL-526: was `this.isTowerCo() ? 1 : 2` — every non-Tower-Co viewer
        // (telco or provider) reads/writes party 2. viewerPartyNo() also
        // covers the single-party-workflow edge case (parties < 2).
        var partyNo = this.viewerPartyNo()

        for (var i = 0; i < this.rowData.steps.length; i++) {
          var s = this.rowData.steps[i]

          if (s.stepNo === current && s.partyNo === partyNo) {
            return s
          }

        }
      }

      return null
    },



    // XOL-534 round 3 (reviewer correction): this function is INERT, and
    // always has been, for every viewer — `this.steps` is populated only by
    // `RequestMixins.workflow()`, whose entries are
    // `{title, icon, colour, comp, data}`: no `stepNo`, no `partyNo`. The
    // `if` below therefore never matches on `main` or here. The earlier
    // comment here claimed the old `s.organisation_id === orgId` clause
    // "could never match [for a provider]" and that dropping it "fixed"
    // syncing `steps[]` — that was wrong: it never matched for ANYONE. The
    // match expression was aligned to `(stepNo, partyNo)` via
    // `viewerPartyNo()` for consistency with `stepData()` above, but this
    // has no runtime effect. Follow-up (plan §8a): delete this method and
    // its call in `stepUpdated`.
    saveStepData (stepData) {
      if (this.steps && this.steps.length > 0) {

        var stepNo = stepData.stepNo
        var partyNo = this.viewerPartyNo()

        for (var i = 0; i < this.steps.length; i++) {
          var s = this.steps[i]

          if (s.stepNo === stepNo && s.partyNo === partyNo) {
            this.steps[i] = stepData
          }

        }
      }

      return null
    },



    partyName (partyNo) {
      if (!partyNo || !this.actionData.steps || !this.actionData.steps[this.activeStep]) {
        return 'Not Found'
      } else {

        for (let i = 0; i < this.actionData.steps.length; i++) {
          let wf = this.actionData.steps[i]
          if (wf.stepNo === this.activeStep && wf.partyNo === partyNo) {
            return (wf.org.shortName ? wf.org.shortName : wf.org.name)
          }

        } // for

        return 'Not Found'
      }
    },



    partyOrg (partyNo) {
      if (!partyNo || !this.actionData.steps || !this.actionData.steps[this.activeStep]) {
        return null
      } else {

        for (let i = 0; i < this.actionData.steps.length; i++) {
          let wf = this.actionData.steps[i]
          if (wf.stepNo === this.activeStep && wf.partyNo === partyNo) {
            return wf.org
          }

        } // for

        return null
      }
    },



    titleColour () {
      switch (this.requestType) {
        case 'rta': return 'text-warning'
        case 'rts': return 'text-success'
        case 'rtu': return 'text-primary'
        case 'rtb': return 'text-info'
        case 'rtd': return 'text-danger'
        default: return 'text-info'
      }
    }

  },   // methods



  computed: {

    // True while a step save is in flight — blocks the drawer close (DrawerBusyMixin).
    drawerBusy: function () {
      return this.savingStep
    },

    // XOL-526: a provider has no emails list of its own (D10 ii) — never
    // render the popup for it. Tower Co and the telco owner are unchanged.
    showEmailSetup: function () {
      return this.isTowerCo() || this.isRequestOwner()
    },

    // XOL-535 (S6, UX §2.6.2): true for a view-only Telco and every Telco on
    // RTS/RTU/RTD (D9 is RTB-only); false for a Telco writer on an RTB, and
    // always false for the Tower Co.
    emailsReadOnly: function () {
      return !this.isTowerCo() && !this.canEditTelcoRecipients()
    },

    configNeeded: function () {
      var config = true
      var i = 0

      if (this.isTelco()) {

        for (i = 0; i < this.emails.telco.length; i++) {
          if (this.emails.telco[i]) {
            config = false
            break
          }
        }

      } else {

        for (i = 0; i < this.emails.tower.length; i++) {
          if (this.emails.tower[i]) {
            config = false
            break
          }
        }
      }  // if..else

      return config
    },

requestsInfo: function () {
      return this.info(this.rowData, this.step, this.activeStep)
    }

  },



  watch: {

    actionData: function (value) {
      this.actionData = value
    },

    state: function (value) {
      this.state = value
    }

  },



  data () {

    return {
      show: 'workflow',
      actionData: this.rowData,
      state: this.rowData.state,
      comp: step1,
      step: this.stepData(),
      // XOL-393: the viewer's own party tab; resolved in created(). Stays a plain data
      // property (not computed) because it is a v-model target on el-tabs.
      // See docs/plans/2026-07-27-xol-393-request-render-access-defects.md (defect 2).
      selectedPartyTab: '1',
      selectedFileTab: 'fileStep' + ((this.rowData && this.rowData.currentStep) ? this.rowData.currentStep : 1),
      readOnly: (this.rowData.status === 'Closed' || this.rowData.status === 'Rejected'),
      requestType: (this.rowData.actionType ? this.rowData.actionType.toLowerCase() : 'rtx'),
      activeStep: (this.rowData && this.rowData.currentStep) ? this.rowData.currentStep : 1,
      reachedStep: (this.rowData && this.rowData.currentStep) ? this.rowData.currentStep : 1,
      fileStep: (this.rowData && this.rowData.currentStep) ? this.rowData.currentStep : 1,
      savingStep: false,
      emails: this.rowData.emails,
      rtxInfo: this.info(this.rowData, this.stepData(), 1),
      steps: null
    }

  },



  created () {
    this.steps = this.workflow(this.step.partyNo, this.actionData.steps)

    // XOL-393: open on the VIEWER's own party (1 = Tower Co, 2 = the counterparty).
    // With no v-model the party tabs always opened on party 1, so a telco saw the
    // Tower Co's files and — because the panes are `lazy` — its own pane was never
    // rendered at all until clicked.
    //
    // The parties guard matters: `action_workflow.parties` can be 1 (PRG is a
    // single-party workflow), and only pane "1" exists then. Selecting "2" would match
    // no pane and el-tabs would render an empty body — a worse failure than the one
    // being fixed. CompletedRequest.vue:29 hides its tab strip outright for that case.
    this.selectedPartyTab = String(this.viewerPartyNo())

    // XOL-393: clamp to the last real step. The API parks actions.currentStep at
    // totalSteps + 1 once every party-1 step is complete (Actions::syncStatusFromSteps),
    // which made activeStep 6 of 5, stepData() return null, and the whole workflow fail
    // to render. Clamps the VIEW only — actionData.currentStep keeps the API's value,
    // which the SPA does not own.
    // See docs/plans/2026-07-27-xol-393-request-render-access-defects.md (defect 1).
    var lastStep = (this.steps && this.steps.length) ? this.steps.length : 1

    if (this.activeStep > lastStep) {
      this.activeStep = lastStep
      this.reachedStep = lastStep
      this.fileStep = lastStep
      this.selectedFileTab = 'fileStep' + lastStep
    }

    if (this.activeStep > 1) {
      this.changeStep(this.activeStep)
    }

    if (this.isNumber(this.step.approved)) {
      this.step.approved = Boolean(this.step.approved)
    }

    if (this.isNumber(this.step.completed)) {
      this.step.completed = Boolean(this.step.completed)
    }

  }

}

</script>

<style scoped>
  .el-select {display: inline-block; width: 100%;}
</style>
